import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MapPin, Crosshair, Layers as LayersIcon, ZoomIn, ZoomOut, Maximize2, X, AlertTriangle, CheckCircle2, Save, Upload, RotateCcw, Download } from "lucide-react";

// RAG → pin color mapping
const RAG_COLORS = {
  good: "#16a34a",        // green
  attention: "#f59e0b",   // amber
  unsafe: "#dc2626",      // red
  struck: "#64748b",      // grey
};
const RAG_LABELS = {
  good: "Good",
  attention: "Needs attention",
  unsafe: "Recommended dismantle / Unsafe",
  struck: "Struck / dismantled",
};

const PLACE_ROLES = ["admin", "contracts_manager", "supervisor"];
const ADMIN_ROLES = ["admin", "contracts_manager"];

export default function SiteMap() {
  const { user } = useAuth();
  const canPlace = PLACE_ROLES.includes(user?.role);
  const canCalibrate = ADMIN_ROLES.includes(user?.role);
  const canBackup = user?.role === "admin";  // strictly admin — full DB export

  const [siteMap, setSiteMap] = useState(null);   // {id, name, image_data_url, anchors, transform, calibrated}
  const [structures, setStructures] = useState([]); // all from /site-map/structures
  const [areas, setAreas] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);

  // viewport
  const [scale, setScale] = useState(0.4);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
  const viewerRef = useRef(null);
  const imgRef = useRef(null);

  // modes
  const [placingFor, setPlacingFor] = useState(null);   // structure id we're placing a pin for
  const [calibrating, setCalibrating] = useState(false);
  const [calibAnchorIdx, setCalibAnchorIdx] = useState(null); // which anchor index to set next
  const [selectedPin, setSelectedPin] = useState(null);  // structure id whose popup is open

  // filters
  const [filterArea, setFilterArea] = useState("");
  const [filterCondition, setFilterCondition] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [smRes, structRes, areaRes] = await Promise.all([
        api.get("/site-maps/active"),
        api.get("/site-map/structures"),
        api.get("/areas").catch(() => ({ data: [] })),
      ]);
      setSiteMap(smRes.data);
      setStructures(structRes.data.structures || []);
      setAreas(areaRes.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Load image dimensions
  useEffect(() => {
    if (!siteMap?.image_data_url) return;
    const im = new Image();
    im.onload = () => {
      setImgSize({ w: im.naturalWidth, h: im.naturalHeight });
      // Fit to viewer
      if (viewerRef.current) {
        const vw = viewerRef.current.clientWidth;
        const vh = viewerRef.current.clientHeight || 600;
        const s = Math.min(vw / im.naturalWidth, vh / im.naturalHeight, 1) * 0.95;
        setScale(s);
        setPan({ x: (vw - im.naturalWidth * s) / 2, y: (vh - im.naturalHeight * s) / 2 });
      }
    };
    im.src = siteMap.image_data_url;
  }, [siteMap?.image_data_url]);

  // Filters
  const filtered = useMemo(() => {
    return structures.filter((s) => {
      if (filterArea && s.area_name !== filterArea) return false;
      if (filterCondition && s.condition_rag !== filterCondition) return false;
      if (filterStatus && s.status !== filterStatus) return false;
      return true;
    });
  }, [structures, filterArea, filterCondition, filterStatus]);
  const pinned = useMemo(() => filtered.filter((s) => s.map_pin), [filtered]);
  const unpinned = useMemo(() => filtered.filter((s) => !s.map_pin), [filtered]);
  const discrepancies = useMemo(() => structures.filter((s) => s.discrepancy), [structures]);

  // Pan/zoom handlers
  const onWheel = (e) => {
    if (!viewerRef.current) return;
    e.preventDefault();
    const rect = viewerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const delta = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(0.05, Math.min(5, scale * delta));
    // zoom toward cursor
    const ratio = newScale / scale;
    setPan({ x: mx - (mx - pan.x) * ratio, y: my - (my - pan.y) * ratio });
    setScale(newScale);
  };
  const dragRef = useRef(null);
  const onMouseDown = (e) => {
    if (placingFor || calibrating) return;
    if (e.button !== 0) return;
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.panX + (e.clientX - dragRef.current.x), y: dragRef.current.panY + (e.clientY - dragRef.current.y) });
  };
  const onMouseUp = () => { dragRef.current = null; };

  // Convert viewer click → image pixel
  const eventToImagePixel = (e) => {
    const rect = viewerRef.current.getBoundingClientRect();
    const vx = e.clientX - rect.left;
    const vy = e.clientY - rect.top;
    const px = (vx - pan.x) / scale;
    const py = (vy - pan.y) / scale;
    return { px, py };
  };

  // Click handler on the image (place pin or calibration)
  const onMapClick = async (e) => {
    if (!viewerRef.current) return;
    // Don't treat drag-release as click
    const { px, py } = eventToImagePixel(e);
    if (px < 0 || py < 0 || px > imgSize.w || py > imgSize.h) return;

    if (placingFor) {
      try {
        await api.patch(`/structures/${placingFor}/map-pin`, { site_map_id: siteMap.id, x: px, y: py });
        setSuccess("Pin placed.");
        setPlacingFor(null);
        refresh();
      } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
      return;
    }
    if (calibrating && calibAnchorIdx !== null) {
      const next = [...siteMap.anchors];
      next[calibAnchorIdx] = { ...next[calibAnchorIdx], px, py };
      try {
        const { data } = await api.patch(`/site-maps/${siteMap.id}/anchors`, { anchors: next });
        setSiteMap({ ...siteMap, anchors: data.anchors, transform: data.transform, calibrated: data.calibrated });
        setSuccess(`Anchor ${siteMap.anchors[calibAnchorIdx].label} placed at (${Math.round(px)}, ${Math.round(py)}).`);
        setCalibAnchorIdx(null);
      } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    }
  };

  const removePin = async (structureId) => {
    if (!window.confirm("Remove this pin from the map?")) return;
    try {
      await api.delete(`/structures/${structureId}/map-pin`);
      setSelectedPin(null);
      setSuccess("Pin removed.");
      refresh();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const updateCondition = async (structureId, rag) => {
    try {
      await api.patch(`/structures/${structureId}/condition`, { condition_rag: rag });
      setSuccess(`Condition updated to ${RAG_LABELS[rag]}.`);
      refresh();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onUploadImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError("Image too large (max 8 MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/site-maps", { name: file.name.replace(/\.[^.]+$/, ""), image_data_url: reader.result });
        await api.post(`/site-maps/${data.id}/activate`);
        setSuccess("New site map activated.");
        refresh();
      } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const fitToView = () => {
    if (!viewerRef.current || !imgSize.w) return;
    const vw = viewerRef.current.clientWidth;
    const vh = viewerRef.current.clientHeight || 600;
    const s = Math.min(vw / imgSize.w, vh / imgSize.h) * 0.95;
    setScale(s);
    setPan({ x: (vw - imgSize.w * s) / 2, y: (vh - imgSize.h * s) / 2 });
  };

  if (loading) return <div style={{ padding: 24 }}>Loading site map…</div>;
  if (!siteMap) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>Site map</h1>
        <p style={{ color: "#64748b" }}>No active site map yet. Upload one to begin.</p>
        {canCalibrate ? <UploadBlock onUpload={onUploadImage} /> : null}
      </div>
    );
  }

  const selectedStructure = selectedPin ? structures.find((s) => s.id === selectedPin) : null;

  return (
    <div data-testid="site-map-page" style={{ padding: "16px 16px 0", height: "calc(100vh - 24px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <header style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 10 }}>
        <div>
          <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 11, letterSpacing: "0.1em" }}>OPERATIONS · GEOREFERENCED SITE MAP</div>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: "2px 0 0" }}>Site map · {siteMap.name}</h1>
          <div className="mono" style={{ fontSize: 11, color: siteMap.calibrated ? "#065f46" : "#92400e", marginTop: 2 }}>
            {siteMap.calibrated ? "✓ CALIBRATED — pins map to real lat/long" : "⚠ NOT CALIBRATED — pins are pixel-only. Use the calibrate tool."}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {user?.role === "mccain_management" ? (
            <a href="/mccain" data-testid="map-back-to-mccain" className="btn-ghost" style={{ fontSize: 12 }}>← Back to McCain dashboard</a>
          ) : null}
          <button data-testid="map-fit-btn" onClick={fitToView} className="btn-ghost"><Maximize2 size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Fit</button>
          <button onClick={() => setScale((s) => Math.min(5, s * 1.2))} className="btn-ghost" title="Zoom in"><ZoomIn size={14} /></button>
          <button onClick={() => setScale((s) => Math.max(0.05, s * 0.8))} className="btn-ghost" title="Zoom out"><ZoomOut size={14} /></button>
          {canCalibrate ? (
            <>
              <button data-testid="map-calibrate-btn" onClick={() => { setCalibrating((c) => !c); setPlacingFor(null); setCalibAnchorIdx(null); }} className={calibrating ? "btn-primary" : "btn-ghost"}>
                <Crosshair size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />{calibrating ? "Stop calibrating" : "Calibrate"}
              </button>
              <label className="btn-ghost" style={{ cursor: "pointer" }}>
                <Upload size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Replace map
                <input data-testid="map-upload-input" type="file" accept="image/*" onChange={(e) => onUploadImage(e.target.files?.[0])} style={{ display: "none" }} />
              </label>
            </>
          ) : null}
          {canBackup ? (
            <a href="/admin/backup" data-testid="map-backup-link" className="btn-ghost" title="Open the dedicated Backup & restore page">
              <Download size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Backup
            </a>
          ) : null}
        </div>
      </header>

      {error ? <Banner type="error" testid="map-error" msg={error} onClose={() => setError("")} /> : null}
      {success ? <Banner type="success" testid="map-success" msg={success} onClose={() => setSuccess("")} /> : null}

      {/* Discrepancy alert — auto-flag-red mismatch */}
      {discrepancies.length > 0 ? (
        <div data-testid="map-discrepancy-alert" className="card" style={{ padding: 10, marginBottom: 8, background: "#fef2f2", borderLeft: "4px solid #dc2626", color: "#7f1d1d", fontSize: 12 }}>
          <AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
          <strong>{discrepancies.length} structure(s) flagged auto-RED by latest inspection</strong> but condition pin is marked safe. Review:{" "}
          {discrepancies.slice(0, 5).map((d, i) => (
            <button key={d.id} onClick={() => setSelectedPin(d.id)} className="btn-ghost" style={{ display: "inline-block", padding: "2px 6px", marginLeft: 4, fontSize: 11 }} data-testid={`map-disc-${d.id}`}>{d.description?.slice(0, 30) || d.reference}{i < Math.min(discrepancies.length, 5) - 1 ? "," : ""}</button>
          ))}
          {discrepancies.length > 5 ? <span style={{ marginLeft: 4 }}>+{discrepancies.length - 5} more</span> : null}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 12, flex: 1, minHeight: 0 }}>
        {/* Sidebar */}
        <aside style={{ overflowY: "auto", paddingRight: 4 }}>
          {/* Filters */}
          <div className="card" style={{ padding: 12, marginBottom: 10 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700 }}>Filters</h3>
            <select data-testid="filter-area" className="input-base" value={filterArea} onChange={(e) => setFilterArea(e.target.value)} style={{ width: "100%", marginBottom: 6 }}>
              <option value="">All areas</option>
              {areas.map((a) => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
            </select>
            <select data-testid="filter-condition" className="input-base" value={filterCondition} onChange={(e) => setFilterCondition(e.target.value)} style={{ width: "100%", marginBottom: 6 }}>
              <option value="">All conditions</option>
              {Object.keys(RAG_LABELS).map((k) => <option key={k} value={k}>{RAG_LABELS[k]}</option>)}
            </select>
            <select data-testid="filter-status" className="input-base" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: "100%" }}>
              <option value="">All statuses</option>
              {["Requested", "RAMS Approved", "DRA Signed", "Erected", "Standing", "Struck"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <div className="mono" style={{ fontSize: 10, color: "#64748b", marginTop: 8 }}>
              SHOWING {filtered.length}/{structures.length} · {pinned.length} pinned · {unpinned.length} un-pinned
            </div>
          </div>

          {/* Legend */}
          <div className="card" style={{ padding: 12, marginBottom: 10 }}>
            <h3 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700 }}>Legend</h3>
            {Object.entries(RAG_LABELS).map(([k, l]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, marginBottom: 3 }}>
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: RAG_COLORS[k], border: "2px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.2)" }} />
                {l}
              </div>
            ))}
          </div>

          {/* Calibration panel */}
          {calibrating && canCalibrate ? (
            <div data-testid="calibrate-panel" className="card" style={{ padding: 12, marginBottom: 10, background: "#fff7ed", border: "1px solid #fdba74" }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#9a3412" }}>Calibrate anchors</h3>
              <div style={{ fontSize: 11, color: "#7c2d12", marginBottom: 8 }}>
                Pair each GPS anchor (A–D) to its pixel position on the plan. Need ≥3 for the transform.
                Sanity check: north-most = highest latitude; west-most = most-negative longitude.
              </div>
              {siteMap.anchors.map((a, i) => (
                <div key={a.label} data-testid={`anchor-row-${a.label}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: i < siteMap.anchors.length - 1 ? "1px dashed #fed7aa" : "none" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{a.label} {a.px !== null && a.py !== null ? <span className="mono" style={{ color: "#065f46", fontSize: 10 }}>✓</span> : <span className="mono" style={{ color: "#92400e", fontSize: 10 }}>·</span>}</div>
                    <div className="mono" style={{ fontSize: 10, color: "#64748b" }}>{a.lat.toFixed(6)}, {a.lng.toFixed(6)}</div>
                    {a.px !== null && a.py !== null ? <div className="mono" style={{ fontSize: 10, color: "#64748b" }}>px {Math.round(a.px)}, {Math.round(a.py)}</div> : null}
                  </div>
                  <button data-testid={`anchor-set-${a.label}`} onClick={() => setCalibAnchorIdx(i)} className={calibAnchorIdx === i ? "btn-primary" : "btn-ghost"} style={{ fontSize: 11, padding: "4px 8px" }}>
                    {calibAnchorIdx === i ? "Click map…" : (a.px !== null ? "Re-set" : "Set")}
                  </button>
                </div>
              ))}
              {siteMap.calibrated ? <div className="mono" style={{ marginTop: 8, padding: 6, background: "#dcfce7", color: "#065f46", fontSize: 10, borderRadius: 3 }}>✓ Transform live</div> : null}
            </div>
          ) : null}

          {/* Un-pinned list (admin bulk-place mode) */}
          {canPlace && unpinned.length > 0 ? (
            <div data-testid="unpinned-panel" className="card" style={{ padding: 12 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700 }}>Un-pinned structures · {unpinned.length}</h3>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>Click a structure then click on the map to drop its pin.</div>
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {unpinned.map((s) => (
                  <button key={s.id} data-testid={`place-pin-${s.id}`}
                    onClick={() => { setPlacingFor(placingFor === s.id ? null : s.id); setCalibrating(false); }}
                    className={placingFor === s.id ? "btn-primary" : "btn-ghost"}
                    style={{ width: "100%", textAlign: "left", padding: "6px 8px", marginBottom: 3, fontSize: 11, display: "flex", alignItems: "center", gap: 6 }}>
                    <MapPin size={12} />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.description}</span>
                    <span className="mono" style={{ fontSize: 9, color: "#64748b" }}>{s.area_name?.slice(0, 16)}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </aside>

        {/* Map viewer */}
        <div ref={viewerRef}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onClick={onMapClick}
          data-testid="map-viewer"
          style={{
            position: "relative", overflow: "hidden", background: "#0f172a",
            border: "1px solid #1e293b", borderRadius: 4,
            cursor: placingFor ? "crosshair" : calibAnchorIdx !== null ? "crosshair" : (dragRef.current ? "grabbing" : "grab"),
            minHeight: 500, userSelect: "none",
          }}>
          {/* placement banner */}
          {placingFor ? (
            <div data-testid="placement-banner" style={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 30, padding: 10, background: "rgba(255,90,0,0.95)", color: "#fff", borderRadius: 3, fontSize: 12, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Placing pin for: <em>{structures.find((s) => s.id === placingFor)?.description}</em> — click on the plan.</span>
              <button onClick={(e) => { e.stopPropagation(); setPlacingFor(null); }} className="btn-ghost" style={{ color: "#fff", border: "1px solid #fff" }}><X size={12} /></button>
            </div>
          ) : null}
          {calibAnchorIdx !== null ? (
            <div data-testid="calibrate-banner" style={{ position: "absolute", top: 8, left: 8, right: 8, zIndex: 30, padding: 10, background: "rgba(154,52,18,0.95)", color: "#fff", borderRadius: 3, fontSize: 12, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Click the position on the plan for anchor <strong>{siteMap.anchors[calibAnchorIdx].label}</strong> ({siteMap.anchors[calibAnchorIdx].lat.toFixed(5)}, {siteMap.anchors[calibAnchorIdx].lng.toFixed(5)})</span>
              <button onClick={(e) => { e.stopPropagation(); setCalibAnchorIdx(null); }} className="btn-ghost" style={{ color: "#fff", border: "1px solid #fff" }}><X size={12} /></button>
            </div>
          ) : null}

          <div style={{ position: "absolute", transformOrigin: "0 0", transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`, pointerEvents: "none" }}>
            <img ref={imgRef} src={siteMap.image_data_url} alt={siteMap.name} draggable={false} style={{ display: "block", maxWidth: "none", userSelect: "none", pointerEvents: "auto" }} />
            {/* Anchor markers in calibration mode */}
            {calibrating ? siteMap.anchors.map((a) => (
              a.px !== null && a.py !== null ? (
                <div key={a.label}
                  data-testid={`anchor-marker-${a.label}`}
                  style={{ position: "absolute", left: a.px, top: a.py, transform: "translate(-50%,-50%)", width: 20 / scale, height: 20 / scale, border: `${3 / scale}px solid #ff5a00`, borderRadius: "50%", background: "rgba(255,90,0,0.2)", pointerEvents: "none" }}>
                  <div className="mono" style={{ position: "absolute", left: "50%", top: "100%", transform: "translateX(-50%)", color: "#ff5a00", background: "#0f172a", padding: `${2 / scale}px ${4 / scale}px`, fontSize: 10 / scale, fontWeight: 700, whiteSpace: "nowrap", marginTop: 2 / scale }}>
                    {a.label}
                  </div>
                </div>
              ) : null
            )) : null}
            {/* Structure pins */}
            {pinned.map((s) => {
              const color = RAG_COLORS[s.condition_rag] || "#64748b";
              const r = Math.max(8, 16 / scale);
              return (
                <button key={s.id}
                  data-testid={`pin-${s.id}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedPin(s.id); }}
                  title={s.description}
                  style={{
                    position: "absolute", left: s.map_pin.x, top: s.map_pin.y,
                    width: r, height: r, transform: "translate(-50%,-50%)",
                    background: color, border: `${Math.max(2, 3 / scale)}px solid #fff`,
                    borderRadius: "50%", padding: 0,
                    boxShadow: s.discrepancy ? `0 0 0 ${Math.max(3, 4 / scale)}px rgba(220,38,38,0.6), 0 ${2/scale}px ${4/scale}px rgba(0,0,0,0.4)` : `0 ${2/scale}px ${4/scale}px rgba(0,0,0,0.4)`,
                    cursor: "pointer", pointerEvents: "auto",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Popup */}
      {selectedStructure ? (
        <StructurePopup s={selectedStructure} canEdit={canPlace} onClose={() => setSelectedPin(null)} onRemove={() => removePin(selectedStructure.id)} onCondition={(rag) => updateCondition(selectedStructure.id, rag)} />
      ) : null}
    </div>
  );
}

function UploadBlock({ onUpload }) {
  return (
    <div className="card" style={{ padding: 16, marginTop: 12 }}>
      <h3 style={{ margin: "0 0 6px" }}>Upload site map (PNG/JPG)</h3>
      <input type="file" accept="image/*" onChange={(e) => onUpload(e.target.files?.[0])} />
    </div>
  );
}

function Banner({ type, msg, testid, onClose }) {
  const bg = type === "error" ? "#fef2f2" : "#ecfdf5";
  const fg = type === "error" ? "#b91c1c" : "#065f46";
  const border = type === "error" ? "#dc2626" : "#059669";
  const Icon = type === "error" ? AlertTriangle : CheckCircle2;
  return (
    <div data-testid={testid} className="card" style={{ padding: 10, marginBottom: 8, background: bg, color: fg, borderLeft: `4px solid ${border}`, display: "flex", gap: 8, alignItems: "center" }}>
      <Icon size={14} /><span style={{ flex: 1, fontSize: 12 }}>{msg}</span>
      {onClose ? <button className="btn-ghost" onClick={onClose}><X size={12} /></button> : null}
    </div>
  );
}

function StructurePopup({ s, canEdit, onClose, onRemove, onCondition }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" data-testid="pin-popup" style={{ background: "white", maxWidth: 520, width: "100%", padding: 20, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, color: "#64748b" }}>{s.reference || s.id?.slice(0, 8)}</div>
            <h2 style={{ margin: "2px 0 0", fontSize: 18, fontWeight: 900 }}>{s.description}</h2>
          </div>
          <button className="btn-ghost" onClick={onClose}><X size={14} /></button>
        </div>

        {s.discrepancy ? (
          <div style={{ padding: 8, background: "#fef2f2", borderLeft: "4px solid #dc2626", color: "#7f1d1d", fontSize: 12, marginBottom: 10 }}>
            <AlertTriangle size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
            <strong>Auto-flagged RED</strong> by latest inspection. {s.discrepancy_reason}
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <Stat label="Area" value={s.area_name} />
          <Stat label="Status" value={s.status} />
          <Stat label="Condition" value={<><span style={{ display: "inline-block", width: 10, height: 10, background: RAG_COLORS[s.condition_rag], borderRadius: "50%", marginRight: 6, verticalAlign: -1 }} />{RAG_LABELS[s.condition_rag]}</>} />
          <Stat label="Latest inspection" value={s.latest_inspection_date ? new Date(s.latest_inspection_date).toLocaleDateString("en-GB") : "—"} />
        </div>

        {canEdit ? (
          <div className="card" style={{ padding: 10, background: "#f8fafc", marginBottom: 10 }}>
            <div className="mono" style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>SET CONDITION (MANUAL)</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.keys(RAG_LABELS).map((k) => (
                <button key={k} data-testid={`set-cond-${k}`} onClick={() => onCondition(k)}
                  className={s.condition_rag_manual === k ? "btn-primary" : "btn-ghost"}
                  style={{ fontSize: 11, padding: "4px 8px", borderLeft: `3px solid ${RAG_COLORS[k]}` }}>
                  {RAG_LABELS[k]}
                </button>
              ))}
            </div>
            <div className="mono" style={{ fontSize: 10, color: "#64748b", marginTop: 6 }}>
              Manual: {RAG_LABELS[s.condition_rag_manual] || "—"} · Auto-derived (shown on map): {RAG_LABELS[s.condition_rag]}
            </div>
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a href={`/structures/${s.id}`} data-testid="popup-link-structure" className="btn-ghost" style={{ fontSize: 12 }}>Open structure →</a>
          <a href={`/rams?structure_id=${s.id}`} data-testid="popup-link-rams" className="btn-ghost" style={{ fontSize: 12 }}>RAMS</a>
          <a href={`/handover?structure_id=${s.id}`} data-testid="popup-link-handover" className="btn-ghost" style={{ fontSize: 12 }}>Handover</a>
          <a href={`/inspections?structure_id=${s.id}`} data-testid="popup-link-inspections" className="btn-ghost" style={{ fontSize: 12 }}>Inspections</a>
          {canEdit ? <button data-testid="popup-remove-pin" onClick={onRemove} className="btn-ghost" style={{ fontSize: 12, color: "#b91c1c", marginLeft: "auto" }}>Remove pin</button> : null}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 9, color: "#64748b", letterSpacing: "0.1em" }}>{(label || "").toUpperCase()}</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{value || "—"}</div>
    </div>
  );
}
